/**
 * §P.8 · El contrato de encargo de tratamiento.
 *
 * ## El `404` **es el dato**, no un fallo
 *
 * El contrato lo dice sin rodeos: «un `404` cuando no está firmado, y a propósito:
 * **que no lo esté es un hecho que el panel tiene que poder enseñar en rojo**, no
 * una ausencia de datos que se pinta en blanco». Así que esta pantalla es la única
 * del panel que trata un 404 como estado normal, y cualquier otro error sí como
 * error — confundirlos diría «sin firmar» cuando lo que pasa es que la red se cayó.
 *
 * ## Qué es esto, en una frase que entienda un maestro de TaeKwonDo
 *
 * **El club es responsable de los datos y Kiap es encargado.** Sin este contrato
 * firmado, que Kiap guarde los datos de ciento veinte menores no tiene base legal —
 * y el que responde delante de la Agencia es el club, no nosotros. Por eso la
 * pantalla lo explica antes de pedir la firma: firmar sin saber qué se firma es
 * exactamente lo que este documento existe para evitar.
 *
 * ## Se firma el texto publicado, y se enseña antes · 12-10-2026
 *
 * La versión sale del modelo vigente (`GET /public/legal/dpa_template`), el mismo
 * texto que se lee en la caja de arriba del formulario. Hasta el 12-10-2026 esta
 * pantalla firmaba una «1.0» escrita a mano que no correspondía a ningún texto
 * publicado, y no enseñaba el texto: se firmaba a ciegas. La API ya solo acepta la
 * vigente (`409 dpa_version_not_current`).
 *
 * Y el alta desde el back office ya no lo da por firmado: lo hacía a nombre del
 * dueño invitado, sin nombre ni IP, y esta pantalla lo enseñaba como «Firmado · Sin
 * nombre» sin dejar firmarlo. Ahora quien lo firma es el dueño, aquí.
 *
 * Si el club firmó una versión anterior, se le enseña la nueva para firmarla: el
 * texto cambió, y lo firmado es lo que dice cada versión.
 *
 * ## Se guarda la IP
 *
 * Y no es burocracia: el nombre, el cargo y la IP son lo único que convierte una
 * fila de una tabla en una firma que se puede defender. La pantalla lo dice antes,
 * porque guardar la IP de alguien sin avisarle sería, en esta pantalla justamente,
 * una ironía cara.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import {
  aProblema,
  ErrorDeApi,
  type Problema,
  noHayNada,
} from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { fecha, fechaYHora } from '@/shared/ui/formato';
import type { ContratoDeEncargo as Contrato } from '@/club/cumplimiento/tipos';

/** El modelo de contrato vigente, el que se lee y se firma. */
interface Modelo {
  readonly version: string;
  readonly title: string;
  readonly body: string;
  readonly effective_from: Date | string;
}

export function ContratoDeEncargo({
  club,
  nombreSugerido,
}: {
  club: string;
  /** El nombre de quien está mirando: firma él, no «el club». */
  nombreSugerido: string;
}) {
  const cache = useQueryClient();
  const [firmando, setFirmando] = useState(false);
  const [nombre, setNombre] = useState(nombreSugerido);
  const [cargo, setCargo] = useState('');
  const [problema, setProblema] = useState<Problema | null>(null);

  const contrato = useQuery({
    queryKey: ['dpa', club],
    /*
     * `null` para el 404 y excepción para todo lo demás. Sin esta distinción, una
     * caída de red pintaría «sin firmar» en rojo a un club que lo tiene firmado, que
     * es la peor forma posible de equivocarse en esta pantalla.
     */
    queryFn: async (): Promise<Contrato | null> => {
      const { data, error } = await api.v1.clubs({ club }).dpa.get();
      if (error) {
        const p = aProblema(error);
        if (noHayNada(p)) return null;
        throw new ErrorDeApi(p);
      }
      return data as unknown as Contrato;
    },
    // Reintentar un 404 que es un dato es gastar tres viajes para saber lo mismo.
    retry: false,
  });

  // Aquí un 404 sí es un fallo: sin modelo publicado no hay nada que leer ni firmar.
  const modelo = useQuery({
    queryKey: ['legal', 'dpa_template'],
    queryFn: async (): Promise<Modelo> => {
      const { data, error } = await api.v1.public
        .legal({ document: 'dpa_template' })
        .get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Modelo;
    },
    staleTime: 5 * 60_000,
  });

  const firmar = useMutation({
    mutationFn: async (version: string) => {
      const r = await api.v1.clubs({ club }).dpa.post({
        version,
        signer_name: nombre.trim(),
        signer_role: cargo.trim() === '' ? null : cargo.trim(),
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setFirmando(false);
      void cache.invalidateQueries({ queryKey: ['dpa', club] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (contrato.isPending || modelo.isPending) {
    return <Cargando filas={4} etiqueta="Cargando el contrato" />;
  }

  if (contrato.isError) {
    return (
      <PanelError
        problema={aProblema(contrato.error)}
        reintentar={() => void contrato.refetch()}
      />
    );
  }

  if (modelo.isError) {
    return (
      <PanelError
        problema={aProblema(modelo.error)}
        reintentar={() => void modelo.refetch()}
      />
    );
  }

  const firmado = contrato.data;
  const vigente = modelo.data;
  const alDia = firmado !== null && firmado.version === vigente.version;

  return (
    <section>
      <h1>Contrato de encargo de tratamiento</h1>

      {/*
        Qué es, antes de pedir la firma: firmar sin saber qué se firma es justo lo
        que este documento existe para evitar.

        **Sin caja de color**, y eso no es cosmética. Esto es explicación, no aviso, y
        con un recuadro ámbar encima del recuadro rojo de «sin firmar» los dos se
        anulan: se vio en el navegador y el rojo dejaba de leerse como rojo. Con dos
        avisos, ninguno avisa.
      */}
      <p className="tenue">
        <strong>
          El club es el responsable de los datos; Kiap, el encargado.
        </strong>{' '}
        Es decir: los datos de los alumnos son del club, y Kiap solo los guarda
        y los trata por encargo suyo y según sus instrucciones.
      </p>
      <p className="tenue">
        Este contrato es lo que da base legal a que Kiap guarde los datos de
        menores. Sin él no la hay, y delante de la Agencia responde el club.
      </p>

      {firmando ? (
        <form
          className="formulario"
          onSubmit={(e) => {
            e.preventDefault();
            setProblema(null);
            firmar.mutate(vigente.version);
          }}
        >
          <h2 className="seccion">
            Firmar la versión {vigente.version}, en vigor desde el{' '}
            {fecha(vigente.effective_from)}
          </h2>

          {/* Lo que se firma, entero y antes del nombre. */}
          <div className="contrato-texto" tabIndex={0}>
            {vigente.body}
          </div>

          <div className="rejilla">
            <label>
              Quién firma
              <input
                required
                maxLength={160}
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
              />
            </label>

            <label>
              Con qué cargo
              <input
                maxLength={80}
                placeholder="Presidente"
                value={cargo}
                onChange={(e) => setCargo(e.target.value)}
              />
            </label>
          </div>

          {/* Avisar de la IP antes, no después: en esta pantalla, sobre todo. */}
          <p className="tenue">
            Se guardan el nombre, el cargo, la hora y <strong>la IP</strong>{' '}
            desde la que se firma. Es lo que convierte una fila en una firma que
            se puede defender.
          </p>

          {problema && <PanelError problema={problema} />}

          <div className="acciones">
            <button type="submit" disabled={firmar.isPending}>
              {firmar.isPending ? 'Firmando…' : 'Firmar el contrato'}
            </button>
            <button
              type="button"
              className="btn-plano"
              disabled={firmar.isPending}
              onClick={() => {
                setFirmando(false);
                setProblema(null);
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      ) : alDia ? (
        <>
          <div className="aviso aviso-calma" role="status">
            <strong>Firmado.</strong> No hay nada más que hacer con esto hasta
            que cambie la versión del contrato.
          </div>

          <DatosDeLaFirma firma={firmado} />

          <details>
            <summary>Leer el contrato firmado</summary>
            <div className="contrato-texto" tabIndex={0}>
              {vigente.body}
            </div>
          </details>
        </>
      ) : (
        <>
          {firmado ? (
            <div className="aviso" role="status">
              <strong>Hay una versión nueva del contrato.</strong> El club firmó
              la {firmado.version}; la vigente es la {vigente.version}, desde el{' '}
              {fecha(vigente.effective_from)}. Léela y fírmala.
            </div>
          ) : (
            // En rojo, que es lo que pide el contrato de la API.
            <div className="aviso aviso-grave" role="alert">
              <strong>Sin firmar.</strong> Mientras siga así, que Kiap guarde
              los datos de los alumnos no tiene base legal. Es de las primeras
              cosas del alta y se tarda medio minuto.
            </div>
          )}

          {firmado && <DatosDeLaFirma firma={firmado} />}

          {problema && <PanelError problema={problema} />}

          <div className="acciones">
            <button type="button" onClick={() => setFirmando(true)}>
              Leer y firmar la versión {vigente.version}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function DatosDeLaFirma({ firma }: { firma: Contrato }) {
  return (
    <dl className="datos">
      <div>
        <dt>Versión</dt>
        <dd>{firma.version}</dd>
      </div>
      <div>
        <dt>Cuándo</dt>
        <dd>{fechaYHora(firma.signed_at)}</dd>
      </div>
      <div>
        <dt>Quién</dt>
        <dd>
          {firma.signer_name ?? 'Sin nombre'}
          {firma.signer_role ? ` · ${firma.signer_role}` : ''}
        </dd>
      </div>
      <div>
        {/* La IP se enseña porque se guardó: esconderla sería raro aquí. */}
        <dt>Desde qué IP</dt>
        <dd>{firma.ip ?? 'No se registró'}</dd>
      </div>
    </dl>
  );
}
